import { TableRow, TableCell } from '@heroui/react';
import React from 'react';
import { FiMessageCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  LoadingTable,
  LoadingTableColumn,
  LinkCell,
} from '../../../../../components/Table';
import { ChatListItem } from '../../../types/chatList';

interface ChatsTableProps {
  chats: readonly ChatListItem[];
  isLoading?: boolean;
}

export const ChatsTable: React.FC<ChatsTableProps> = ({
  chats,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const loadingTableColumns: LoadingTableColumn[] = [
    { label: 'Buyer', className: 'pl-6' },
    { label: 'Buyer users', className: 'hidden md:table-cell' },
    { label: 'Distributor', className: 'hidden lg:table-cell' },
    { label: 'Distributor users', className: 'hidden lg:table-cell' },
    { label: 'Chat name' },
    { label: 'Restaurant name', className: 'hidden md:table-cell' },
    { label: 'Customer number', className: 'hidden lg:table-cell' },
    { label: 'Delivery address', className: 'hidden xl:table-cell' },
  ];

  const handleRowClick = (chatId: string) => {
    navigate(`/chats/${chatId}`);
  };

  const renderRow = (chat: ChatListItem) => (
    <TableRow
      key={chat.id}
      className="hover:bg-default-50 dark:hover:bg-default-100/50 transition-colors duration-200 cursor-pointer"
      onClick={() => handleRowClick(chat.id)}
    >
      <TableCell className="pl-6">
        {chat.buyerEntity ? (
          <LinkCell to={`/buyers/${chat.buyerEntity.id}`}>
            {chat.buyerEntity.name}
          </LinkCell>
        ) : (
          <span className="text-default-500">-</span>
        )}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <div className="space-y-1">
          {chat.buyerUsers.length > 0 ? (
            chat.buyerUsers.map((user) => (
              <div key={user.id} className="text-sm">
                <LinkCell to={`/users/${user.id}`} className="text-sm">
                  {user.name}
                </LinkCell>
              </div>
            ))
          ) : (
            <span className="text-default-500">-</span>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {chat.supplierEntity ? (
          <LinkCell to={`/suppliers/${chat.supplierEntity.id}`}>
            {chat.supplierEntity.name}
          </LinkCell>
        ) : (
          <span className="text-default-500">-</span>
        )}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <div className="space-y-1">
          {chat.supplierUsers.length > 0 ? (
            chat.supplierUsers.map((user) => (
              <div key={user.id} className="text-sm">
                <LinkCell to={`/users/${user.id}`} className="text-sm">
                  {user.name}
                </LinkCell>
              </div>
            ))
          ) : (
            <span className="text-default-500">-</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <span className="font-medium">{chat.chatName}</span>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {chat.restaurantName}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {chat.customerNumber || '-'}
      </TableCell>
      <TableCell className="hidden xl:table-cell pr-6">
        <div className="max-w-xs truncate" title={chat.address}>
          {chat.address || '-'}
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <LoadingTable
      columns={loadingTableColumns}
      ariaLabel="Chats table"
      className="mb-6"
      isLoading={isLoading}
      isEmpty={chats.length === 0}
      data={[...chats]}
      renderRow={(chat) => renderRow(chat as ChatListItem)}
      emptyState={{
        icon: FiMessageCircle,
        iconBgClass: 'bg-primary-50',
        iconColorClass: 'text-primary-500',
        title: 'No chats found',
        description: 'Try adjusting your search or filters.',
      }}
    />
  );
};
